import { addDays, compareDates, type LocalDate } from '../dates';

const DAY_MS = 86_400_000;

function utcMidnight(d: LocalDate): number {
  return Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
}

/** Whole calendar days from `from` to `to` (negative if `to` is earlier). */
export function dayDiff(from: LocalDate, to: LocalDate): number {
  return Math.round((utcMidnight(to) - utcMidnight(from)) / DAY_MS);
}

/** Every local day from start to end, inclusive. */
export function eachDate(start: LocalDate, end: LocalDate): LocalDate[] {
  const out: LocalDate[] = [];
  for (let d = start; compareDates(d, end) <= 0; d = addDays(d, 1)) out.push(d);
  return out;
}

/** Offset of `tz` from UTC at `instantMs`, in ms (Asia/Manila = +8h). */
function tzOffsetMs(instantMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instantMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const wallAsUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return wallAsUtc - Math.floor(instantMs / 1000) * 1000;
}

/** The last millisecond of local day `date` in `tz`, as an ISO UTC instant. */
export function endOfLocalDay(date: LocalDate, tz: string): string {
  const guess = Date.parse(`${date}T23:59:59.000Z`);
  return new Date(guess - tzOffsetMs(guess, tz) + 999).toISOString();
}

/** Whole minutes from one instant to another, never negative. */
export function minutesBetween(fromIso: string, toIso: string): number {
  return Math.max(0, Math.floor((Date.parse(toIso) - Date.parse(fromIso)) / 60_000));
}
